/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable functional/immutable-data */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines-per-function */
import { ChangeEvent, useEffect, useRef } from 'react'
import { useLang } from '../../../../../../hooks/useLang'
import {
  Grid,
  Button,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  
  InputAdornment,
  Box,
  TextField,
} from '@mui/material'
import InputOccurrenceNumber from '~/components/InputOccurrenceNumber'
import { toDomain } from '~/mappers/surveillance-reporting-mapper'
import useUserState from '~/features/User/stores/UserState'
import CancelIcon from '@mui/icons-material/Cancel'
import useSurveillanceReportingState from '../../../../stores/SurveillanceReportingState'
import { useHookstate } from '@hookstate/core'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import SearchIcon from '@mui/icons-material/Search'
import FilterKeyword from '~/features/Report/components/FilterKeyword'
import FilterSwitch from '~/components/FilterSwitch'

interface FilterItem {
  readonly field: string
  readonly operator: string
  readonly value: string | number | null | readonly string[]
}

interface FiltersProps {
  readonly content: ReadonlyArray<FilterItem>
  readonly fields: {
    readonly camera_name: string | null
    readonly occurrence_id: string | null
    readonly startDate: string | null
    readonly finishDate: string | null
    readonly keyWords: string | null
    readonly includeAll: boolean | null
    readonly user_id: number | null
  }
}

const filtersDefault: FiltersProps = {
  content: [],
  fields: {
    camera_name: null,
    occurrence_id: null,
    startDate: null,
    finishDate: null,
    keyWords: '',
    includeAll: null,
    user_id: null,
  },
}

interface FilterParams {
  readonly startDate?: string
  readonly finishDate?: string
  readonly keyWords?: readonly string[]
  readonly includeAll?: boolean
  readonly occurrenceId?: number
  readonly camera?: string
  readonly userId?: number
  readonly page: number
  readonly limit: number
}

// eslint-disable-next-line max-lines-per-function
interface VideoMonitoringFiltersProps {
  readonly onSearch: (isSearch?: boolean) => void  
  readonly onFilterCountChange: (count: number) => void
  readonly onFiltersChange?: (filters: FilterParams) => void
}

export default function VideoMonitoringFilters({ onSearch, onFilterCountChange, onFiltersChange }: VideoMonitoringFiltersProps) {
  const { translate } = useLang()
  const { surveillanceReportingStates, listSurveillanceReporting } =
    useSurveillanceReportingState()
  const { reports, switchCheck, loading } = surveillanceReportingStates()
  const { user: userSession } = useUserState()
  const startDate = useHookstate<string | null>(null)
  const finishDate = useHookstate<string | null>(null)
  const period = useHookstate<string>('')
  const filters = useHookstate(filtersDefault)
  const occurrenceNumber = filters.fields.occurrence_id
  const comments = useHookstate<string | null>(null)
  const includeAllWords = useHookstate<boolean>(false)
  const lockFields = useHookstate<boolean>(false)
  
  // Usar ref para evitar recriações de timeout
  const filterCountTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const FILTERS_STORAGE_KEY = 'videoMonitoring_filters'

  // Inicialização única
  useEffect(() => {
    try {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY)
    } catch (error) {
      console.error('Erro ao limpar filtros do storage:', error)
    }
  }, [])

  // Otimizar o cálculo do contador com debounce simples
  useEffect(() => {
    if (filterCountTimeoutRef.current) {
      clearTimeout(filterCountTimeoutRef.current)
    }

    filterCountTimeoutRef.current = setTimeout(() => {
      let count = 0
      
      const cameraName = filters.fields.camera_name.get()
      const occurrenceId = filters.fields.occurrence_id.get()
      const startDateVal = filters.fields.startDate.get()
      const finishDateVal = filters.fields.finishDate.get()
      const keyWordsVal = filters.fields.keyWords.get()
      const includeAllVal = filters.fields.includeAll.get()
      const switchVal = switchCheck.get()
      
      if (cameraName?.trim()) count++
      if (occurrenceId?.trim()) count++
      if (startDateVal) count++
      if (finishDateVal) count++
      if (keyWordsVal?.trim()) count++
      if (includeAllVal === true) count++
      if (switchVal) count++
      
      onFilterCountChange(count)
    }, 200)

    return () => {
      if (filterCountTimeoutRef.current) {
        clearTimeout(filterCountTimeoutRef.current)
      }
    }
  }, [
    filters.fields.camera_name.get(),
    filters.fields.occurrence_id.get(),
    filters.fields.startDate.get(),
    filters.fields.finishDate.get(),
    filters.fields.keyWords.get(),
    filters.fields.includeAll.get(),
    switchCheck.get(),
  ])

  // Simplificar o useEffect do switchCheck
  useEffect(() => {
    const session = userSession()
    const userId = session?.id?.get ? (session.id.get() as number) : undefined
    
    if (switchCheck.get() && userId != null) {
      filters.fields.user_id.set(userId)
    } else if (!switchCheck.get()) {
      filters.fields.user_id.set(null)
    }
  }, [switchCheck.get()]) // Apenas switchCheck como dependência

  const buildFilterParams = (pageQuery: number): FilterParams => {
    const keyWordsStr = filters.fields.keyWords.get()
    return {
      startDate: filters.fields.startDate.get() || undefined,
      finishDate: filters.fields.finishDate.get() || undefined,
      keyWords: keyWordsStr
        ? keyWordsStr
            .split(';')
            .map((keyword) => keyword.trim())
            .filter((keyword) => keyword)
        : undefined,
      includeAll: filters.fields.includeAll.get() || undefined,
      occurrenceId: filters.fields.occurrence_id.get()
        ? Number(filters.fields.occurrence_id.get())
        : undefined,
      camera: filters.fields.camera_name.get() || undefined,
      userId: filters.fields.user_id.get() || undefined,
      page: pageQuery,
      limit: 10,
    }
  }

  const nextData = (reset = false) => {
    loading.set(true)
    const pageQuery = reset ? 0 : Math.floor(reports.get().length / 10)
    
    const filterParams = buildFilterParams(pageQuery)
    
    listSurveillanceReporting(filterParams).then(({ data }) => {
      if (reset) {
        const toDomainsReport = data?.map((report: typeof data[0]) => toDomain(report))
        reports.set(toDomainsReport)
      } else {
        const toDomainsReport = data?.map((report: typeof data[0]) => toDomain(report))
        reports.merge(toDomainsReport)
      }
      loading.set(false)
    })
  }

  const handleClearInputs = () => {
    // Limpar todos os filtros de uma vez para evitar múltiplas renderizações
    filters.set({
      content: [],
      fields: {
        camera_name: null,
        occurrence_id: null,
        startDate: null,
        finishDate: null,
        keyWords: '',
        includeAll: null,
        user_id: null,
      },
    })
    
    switchCheck.set(true)
    period.set('')
    startDate.set(null)
    finishDate.set(null)
    
    // Configurar user_id após limpar
    const session = userSession()
    const userId = session?.id?.get ? (session.id.get() as number) : undefined
    if (userId) {
      filters.fields.user_id.set(userId)
    }
    
    try {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY)
    } catch (error) {
      console.error('Erro ao limpar filtros do storage:', error)
    }
    
    const defaultFilterParams: FilterParams = {
      page: 0,
      limit: 10,
      userId: userId,
    }
    
    onFiltersChange?.(defaultFilterParams)
    onSearch(true)
    nextData(true)
  }

  const actionFilter = () => {
    reports.set([])
    onSearch(true)
    
    const filterParams = buildFilterParams(0)
    onFiltersChange?.(filterParams)
    nextData(true)
  }

  return (
    <Grid container columns={12}>
      <Grid item xs={12} sx={{ padding: 1 }}>
        <FormControlLabel
          sx={{ paddingLeft: '8px' }}
          control={
            <Switch
              checked={switchCheck.get()}
              onChange={() => switchCheck.set(!switchCheck.get())}
            />
          }
          label={translate('only my narrations')}
        />
      </Grid>

      <Grid
        container
        xs={12}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Grid container direction={'row'} sx={{ paddingRight: 1 }}>
          <Grid item xs={6} sx={{ paddingLeft: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <InputOccurrenceNumber
              occurrence={occurrenceNumber}
              onOccurrenceChange={(event: ChangeEvent<HTMLInputElement>) => {
                occurrenceNumber?.set(event.target.value)
              }}
            />
          </Grid>
          <Grid item xs={6} sx={{ paddingLeft: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                label={translate('Camera')}
                value={filters.fields.camera_name.get() ?? ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  filters.fields.camera_name.set(event.target.value)
                }
                InputProps={{
                  endAdornment: filters.fields.camera_name.get() ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => filters.fields.camera_name.set(null)}
                        edge="end"
                        aria-label={translate('Clear camera')}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>
          </Grid>
          <FilterSelectPeriod
            onStartDateChange={(value) => {
              startDate.set(value?.toJSON() ?? null)
              filters.fields.startDate.set(startDate.get())
            }}
            onFinishDateChange={(value) => {
              finishDate.set(value?.toJSON() ?? null)
              filters.fields.finishDate.set(finishDate.get())
            }}
            onPeriodChange={(value) => {
              period.set(value)
            }}
            period={period.get()}
            xsPeriod={4}
            xsDateBetween={4}
          />

          <Grid item xs={12} pl={1} pt={1}>
            <FilterKeyword keyWords={filters.fields.keyWords} />
          </Grid>
          {filters.fields.keyWords.get() && filters.fields.keyWords.get()?.trim() !== '' && (
            <Grid item xs={12} sx={{ pt: 1, pl: 2 }}>
              <FilterSwitch
                label={translate('Contain all keywords')}
                onCheckedChange={() => {
                  filters.fields.includeAll.set(!filters.fields.includeAll.get())
                }}
                disabled={lockFields.get()}
                checked={filters?.fields?.includeAll?.get() ?? false}
                tooltip={'All words must exist in the comment'}
                size='medium'
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}
      >
        <Button
          variant="text"
          onClick={handleClearInputs}
          sx={{
            height: '40px',
            textTransform: 'none',
            textDecorationLine: 'underline',
            color: 'text.primary',
            ':hover': {
              bgcolor: 'transparent',
              color: 'primary.main',
            },
          }}
        >
          {translate('Clear Filters')}
        </Button>
        <Tooltip title={translate('Apply Filters')}>
          <IconButton onClick={actionFilter} disabled={loading.get()}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  )
}
